import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../services/api';
import { AuthBrand } from '../components/AuthBrand';
import { StaffLoginCodeForm } from '../components/StaffLoginCodeForm';
import { TotpCodeForm } from '../components/TotpCodeForm';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

type LoginStage = 'credentials' | 'code' | 'totp';

function navigateAfterLogin(role: 'USER' | 'ADMIN' | undefined, navigate: ReturnType<typeof useNavigate>) {
  if (role === 'ADMIN') {
    navigate('/admin/applications');
    return;
  }
  navigate('/');
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyLoginCode, verifyLogin2FA, resendLoginCode, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [loginStage, setLoginStage] = useState<LoginStage>('credentials');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.requiresLoginCode) {
        setLoginStage('code');
        return;
      }
      if (result.requires2FA) {
        setLoginStage('totp');
        return;
      }
      navigateAfterLogin(result.role, navigate);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 403 && err.message.includes('not activated')) {
        navigate(`/activate?email=${encodeURIComponent(email)}`);
        return;
      }
      setLocalError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setLocalError(null);
    setIsLoading(true);
    try {
      const result = await verifyLoginCode(email, code);
      if (result.requires2FA) {
        setLoginStage('totp');
        return;
      }
      navigateAfterLogin(result.role, navigate);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyTotp = async (code: string) => {
    setLocalError(null);
    setIsLoading(true);
    try {
      const result = await verifyLogin2FA(email, code);
      navigateAfterLogin(result.role, navigate);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="relative w-full max-w-md animate-fade-in">
        <AuthBrand />
        <div className="bg-background-secondary border border-border rounded-xl p-8 shadow-xl">
          {loginStage === 'totp' ? (
            <TotpCodeForm
              email={email}
              onVerify={handleVerifyTotp}
              onBack={() => {
                setLoginStage('credentials');
                setLocalError(null);
              }}
              error={displayError}
              isLoading={isLoading}
            />
          ) : loginStage === 'code' ? (
            <StaffLoginCodeForm
              email={email}
              onVerify={handleVerifyCode}
              onResend={() => resendLoginCode(email)}
              onBack={() => {
                setLoginStage('credentials');
                setLocalError(null);
              }}
              error={displayError}
              isLoading={isLoading}
            />
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-xl font-semibold text-foreground mb-2">Admin sign in</h1>
                <p className="text-sm text-foreground-secondary">Manage registered applications</p>
              </div>

              {displayError && (
                <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
                  {displayError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">Email</label>
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="you@tyaus.com" required autoComplete="email" autoFocus />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="label">Password</label>
                    <Link to="/forgot-password" className="text-xs text-accent hover:text-accent-hover">Forgot password?</Link>
                  </div>
                  <div className="relative">
                    <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="input pr-10" placeholder="••••••••" required autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-2.5">
                  {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in...</> : 'Sign in'}
                </button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-foreground-secondary">Don't have an account? </span>
                <Link to="/signup" className="text-accent hover:text-accent-hover">Sign up</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
